/**
 * Whether the person may open a registered route, or holds a form's capability, in the scope open now — the portal host's
 * `canOpen` (plan-019 WAPP-HOST; FR-HA-17, FR-HA-21; design §31.9.2; REGY's `HELP_ROUTE_REGISTRY`).
 *
 * Asked of ADR 0033's canonical evaluator (`evaluateCapability`), never restated: the grants are the auth_session's roles
 * in the open organization and workspace (the shell's capability state), the target is the open workspace or
 * organization — a navigation can only ever land in the current scope (the portal's paths name none) — and the resource
 * state is the auth_session's organization state and the open workspace's state from its fresh access listing.
 *
 * WHAT IS NOT KNOWN HERE, AND WHY IT DOES NOT MATTER. The page knows no step-up instant and no sign-in policy revision:
 * the assurance passed is "none", which the evaluator reads only for a capability that needs a step-up (none of the
 * registered routes' does — a test holds that; such a capability answers `false` here rather than guessing) or a
 * policy the target enforces (the server enforced it at sign-in and enforces it again on every request). A workspace
 * whose state the listing does not name as one the evaluator knows answers `false`: offered nothing, refused nothing
 * the server would allow. **This decides what is OFFERED, never what is allowed** (`shell/member-capability.ts`): the
 * route's own guard and the API decide again when the person acts.
 *
 * During impersonation the widget is not mounted at all (design §31.9.6); an operator's session answers `false`.
 */
import {
  HELP_ROUTE_REGISTRY,
  evaluateCapability,
  isAccountCapability,
  needsStepUp,
  NO_GRANTS,
  type AccountId,
  type CapabilityQuestion,
  type HelpRouteId,
  type NamedCapability,
  type PersonId,
  type WorkspaceId,
} from "@wetalk/domain";

import type { AuthSessionView } from "../api/gateway.js";

/** The workspace states the evaluator knows (`ResourceState.workspaceState`). */
const WORKSPACE_STATE = ["active", "archived", "draining"] as const;
type KnownWorkspaceState = (typeof WORKSPACE_STATE)[number];

function workspaceStateOf(current: AuthSessionView): KnownWorkspaceState | null | "unknown" {
  if (current.kind !== "workspace_selected") return null;
  const state = current.accountChoice
    .find((one) => one.accountId === current.accountId)
    ?.workspace.find((one) => one.workspaceId === current.workspaceId)?.state;
  const known = WORKSPACE_STATE.find((one) => one === state);
  return known === undefined ? "unknown" : known;
}

/**
 * Whether the signed-in person holds `capability` in the scope open now: a workspace capability in the open workspace,
 * an organization capability in the open organization. `null` (signed out) and an operator acting as the person hold
 * nothing.
 */
export function holdsInOpenScope(current: AuthSessionView | null, capability: NamedCapability, now: Date): boolean {
  if (current === null || current.kind === "selection_required" || current.actor.kind !== "self") return false;
  if (needsStepUp(capability)) return false;
  const workspaceState = workspaceStateOf(current);
  if (workspaceState === "unknown") return false;
  const accountId = current.accountId as AccountId;
  const account = isAccountCapability(capability);
  if (!account && current.kind !== "workspace_selected") return false;
  const question: CapabilityQuestion = {
    principal: {
      kind: "person",
      personId: current.person.personId as PersonId,
      assurance: { mfaVerifiedAt: null, steppedUpAt: null, signInPolicyRevision: null },
    },
    grants: {
      ...NO_GRANTS,
      accountRole: current.accountRole,
      memberRole: current.kind === "workspace_selected" ? current.memberRole : null,
    },
    target:
      account || current.kind !== "workspace_selected"
        ? { kind: "account", accountId }
        : { kind: "workspace", accountId, workspaceId: current.workspaceId as WorkspaceId },
    capability,
    resourceState: { accountState: current.accountState, workspaceState, workspaceCreationPolicy: null },
    requiredPolicyRevision: null,
    now,
    stepUpWindowSecond: null,
  };
  return evaluateCapability(question).allowed;
}

/**
 * Whether the person may open the registered route now: its scope must be open (a workspace route needs a workspace,
 * an organization route an organization), a way-in page (`requiresSignIn: false`) is offered only to a visitor who is
 * not signed in, and a route's capability is asked of the evaluator in the open scope.
 */
export function canOpenHelpRoute(current: AuthSessionView | null, routeId: HelpRouteId, now: Date): boolean {
  const route = HELP_ROUTE_REGISTRY[routeId];
  if (!route.requiresSignIn) return current === null;
  if (current === null || current.actor.kind !== "self") return false;
  const scope: readonly string[] = route.param;
  if (scope.includes("workspace") && current.kind !== "workspace_selected") return false;
  if (scope.includes("organization") && current.kind === "selection_required") return false;
  if (route.capability === null) return true;
  return holdsInOpenScope(current, route.capability, now);
}
